import {
  type BiomeId,
  type BossAbilityKind,
  type BossImpactEvent,
  type BossTelegraphEvent,
  TILE_SIZE,
  type TilePosition,
} from '@shefira/shared';
import type Phaser from 'phaser';

import { puffSmoke } from './smoke-puff.js';

/**
 * What a zone boss's ability looks like (ADR 0024, ADR 0029): the shape the
 * server marked, tile by tile, then the blow. Drawn from code in whole
 * pixels like the players' spells (`spell-effects.ts`), never a smoothed
 * shape (CLAUDE.md rule 6), and exactly on the tiles the server sent: a
 * player reads where to step. Nothing here decides who is hit.
 *
 * The warning reads its own clock: the shape's tiles light up from where the
 * blow comes from — the boss, or the target for a slam — and the blow lands
 * when the last one is lit, while the outline blinks faster. Each shape adds
 * its own gesture: brackets closing on a slam's tile, waves running out of a
 * nova or a ring, chevrons racing down a line or the arms of a cross, and
 * the shadows of what is about to fall growing on a rain's drops. The blow
 * takes the colours of the boss's land.
 */

/** Just over the ground and the move marker, under every standing sprite. */
const DISC_DEPTH = -390;
/** Palette colours (data/assets/palette.gpl). */
const WARNING = 0xff3b3b;
const WARNING_DARK = 0x8a1a1a;
const WHITE = 0xf8f2ea;
const SOOT = 0x2a1c1a;
const IMPACT_MS = 560;
/** Debris thrown up at most: a big ring must not flood the screen. */
const DEBRIS_MAX = 40;

/** The colours of each land's debris, from the brightest to the darkest, and its wave. */
const FLAVOURS: Record<BiomeId, { debris: readonly number[]; wave: number }> = {
  meadow: { debris: [0xfff1a8, 0xe0b040, 0x8a6a2a, SOOT], wave: 0xe0b040 },
  forest: { debris: [WHITE, 0xc9c9da, 0x6b6b78, SOOT], wave: 0xc9c9da },
  swamp: { debris: [0x9ab060, 0x5b6b3a, 0x3f5a2e, SOOT], wave: 0x7a8a48 },
  desert: { debris: [0xfff1c8, 0xe6c680, 0xc09a50, 0x8a6a2a], wave: 0xe6c680 },
  snow: { debris: [WHITE, 0xbfe3ff, 0x6fb8ff, 0x3a6fb0], wave: 0xbfe3ff },
};

/** A shape ready to draw: its tiles, where the blow comes from, how far its farthest tile lies. */
interface Shape {
  kind: BossAbilityKind;
  tiles: readonly TilePosition[];
  set: ReadonlySet<string>;
  origin: TilePosition;
  reach: number;
  radius: number;
  /** A rain's drops: the tiles whose whole square is in the shape. */
  drops: TilePosition[];
}

const warningKey = (event: { mobId: string; kind: BossAbilityKind }): string =>
  `${event.mobId}:${event.kind}`;
const keyOf = (tile: TilePosition): string => `${String(tile.x)},${String(tile.y)}`;
const cheb = (a: TilePosition, b: TilePosition): number =>
  Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

function shapeOf(event: BossTelegraphEvent | BossImpactEvent): Shape {
  const set = new Set(event.tiles.map(keyOf));
  const origin = event.centre;
  const reach = Math.max(1, ...event.tiles.map((t) => cheb(t, origin)));
  const drops =
    event.kind === 'rain'
      ? event.tiles.filter((t) => {
          for (let dy = -event.radius; dy <= event.radius; dy += 1) {
            for (let dx = -event.radius; dx <= event.radius; dx += 1) {
              if (!set.has(keyOf({ x: t.x + dx, y: t.y + dy }))) return false;
            }
          }
          return true;
        })
      : [];
  return { kind: event.kind, tiles: event.tiles, set, origin, reach, radius: event.radius, drops };
}

/** Centre of a tile on screen. */
const px = (tile: TilePosition): { x: number; y: number } => ({
  x: tile.x * TILE_SIZE + TILE_SIZE / 2,
  y: tile.y * TILE_SIZE + TILE_SIZE / 2,
});

export class BossEffects {
  /**
   * One warning per boss and shape at most: a new one replaces what was left
   * of the last. A stun (ADR 0048) sends the same attack again with the time
   * left, so the replacement goes on from where the warning had got to.
   * Keyed by shape too: a big raid's Shefira marks two shapes at once (ADR 0068).
   */
  private readonly warnings = new Map<
    string,
    {
      graphics: Phaser.GameObjects.Graphics;
      tween: Phaser.Tweens.Tween;
      drawn: { progress: number };
    }
  >();

  constructor(private readonly scene: Phaser.Scene) {}

  /** Draws the warning; false when it only put off one already on the ground (a stun). */
  telegraph(event: BossTelegraphEvent, flavour: BiomeId = 'meadow'): boolean {
    const key = warningKey(event);
    const previous = this.warnings.get(key);
    previous?.tween.stop();
    previous?.graphics.destroy();
    const shape = shapeOf(event);
    const graphics = this.scene.add.graphics().setDepth(DISC_DEPTH);
    const wave = FLAVOURS[flavour].wave;
    const drawn = { progress: previous?.drawn.progress ?? 0 };
    const tween = this.scene.tweens.addCounter({
      from: drawn.progress,
      to: 1,
      duration: event.ms,
      onUpdate: (counter) => {
        const p = counter.getValue() ?? 0;
        drawn.progress = p;
        graphics.clear();
        drawWarning(graphics, shape, p);
        switch (shape.kind) {
          case 'slam':
            drawBrackets(graphics, px(shape.origin), shape.radius, p);
            break;
          case 'nova':
          case 'ring':
            drawWaves(graphics, shape, p, wave);
            break;
          case 'line':
          case 'cross':
            drawChevrons(graphics, shape, p);
            break;
          case 'rain':
            drawShadows(graphics, shape, p);
            break;
        }
      },
    });
    const entry = { graphics, tween, drawn };
    this.warnings.set(key, entry);
    // A server that never lands it (the boss fell mid-swing) leaves no stain.
    this.scene.time.delayedCall(event.ms + 400, () => {
      tween.stop();
      if (this.warnings.get(key) === entry) this.warnings.delete(key);
      graphics.destroy();
    });
    return previous === undefined;
  }

  /**
   * The shape lands: a flash over its tiles, the land's debris thrown up,
   * the shape's own blow (a shockwave, cracks, falling hail), and a jolt of
   * the camera when the player stood close enough to feel it.
   */
  impact(
    event: BossImpactEvent,
    player: { x: number; y: number } | undefined,
    flavour: BiomeId = 'meadow',
  ): void {
    const key = warningKey(event);
    const warning = this.warnings.get(key);
    warning?.tween.stop();
    warning?.graphics.destroy();
    this.warnings.delete(key);
    const shape = shapeOf(event);
    const { debris, wave } = FLAVOURS[flavour];
    const origin = px(shape.origin);
    const grains = scatter(shape);
    const fall = shape.kind === 'rain' ? 0.35 : 0;

    this.animate(DISC_DEPTH, IMPACT_MS, (graphics, p) => {
      // Rain: the flash waits for what falls to land.
      const q = fall === 0 ? p : Math.max(0, (p - fall) / (1 - fall));
      if (q > 0) {
        for (const tile of shape.tiles) {
          graphics.fillStyle(WHITE, Math.max(0, 0.7 - q * 1.8));
          graphics.fillRect(tile.x * TILE_SIZE, tile.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        }
      }
      switch (shape.kind) {
        case 'slam':
          drawCracks(graphics, origin, p);
          ring(
            graphics,
            origin.x,
            origin.y,
            4 + p * ((shape.radius + 1) * TILE_SIZE + 8),
            fade(p, 0.3),
            wave,
            p,
          );
          break;
        case 'nova':
        case 'ring': {
          const far = (shape.reach + 1) * TILE_SIZE;
          ring(graphics, origin.x, origin.y, p * far, fade(p, 0.25), wave, p);
          const late = Math.max(0, p - 0.18);
          ring(graphics, origin.x, origin.y, late * far, fade(late, 0.25), wave, 1);
          break;
        }
        case 'line':
        case 'cross':
          // A streak runs out from the boss along every arm, the ground cracking behind it.
          for (const tile of shape.tiles) {
            const d = cheb(tile, shape.origin) / shape.reach;
            if (d > p * 1.4) continue;
            const c = px(tile);
            graphics.fillStyle(d > p * 1.2 ? WHITE : SOOT, fade(p, 0.5) * 0.8);
            graphics.fillRect(Math.round(c.x) - 1, Math.round(c.y), 3, 1);
          }
          break;
        case 'rain':
          for (const drop of shape.drops) {
            const c = px(drop);
            // What falls: a streak from high above, landing at `fall`.
            if (p < fall) {
              const h = Math.round(90 * (1 - p / fall));
              graphics.fillStyle(wave, 1);
              graphics.fillRect(Math.round(c.x), Math.round(c.y) - h - 6, 1, 6);
              graphics.fillStyle(WHITE, 1);
              graphics.fillRect(Math.round(c.x), Math.round(c.y) - h, 1, 2);
            } else {
              ring(
                graphics,
                c.x,
                c.y,
                3 + q * (shape.radius + 1) * TILE_SIZE,
                fade(q, 0.3),
                wave,
                q,
              );
            }
          }
          break;
      }
    });
    this.animate(Math.round(origin.y) + TILE_SIZE, IMPACT_MS, (graphics, p) => {
      const t = fall === 0 ? p : (p - fall) / (1 - fall);
      if (t <= 0) return;
      graphics.setAlpha(fade(t, 0.6));
      for (const grain of grains) {
        const x = grain.x + grain.vx * t;
        const y = grain.y + grain.vy * t + grain.g * t * t;
        const color = debris[Math.min(debris.length - 1, Math.floor(t * debris.length))] ?? SOOT;
        graphics.fillStyle(color, 1);
        graphics.fillRect(Math.round(x), Math.round(y), grain.size, grain.size);
      }
    });
    if (shape.kind !== 'rain')
      puffSmoke(this.scene, { x: Math.round(origin.x), y: Math.round(origin.y) + 6 }, origin.y + 7);
    if (player === undefined) return;
    const near = shape.tiles.some((tile) => cheb(tile, player) <= 3);
    if (near) this.scene.cameras.main.shake(180, 0.005);
  }

  destroy(): void {
    for (const warning of this.warnings.values()) {
      warning.tween.stop();
      warning.graphics.destroy();
    }
    this.warnings.clear();
  }

  /** Run `draw(progress)` on a fresh graphics every frame for `durationMs`, then clear it away. */
  private animate(
    depth: number,
    durationMs: number,
    draw: (graphics: Phaser.GameObjects.Graphics, progress: number) => void,
  ): void {
    const graphics = this.scene.add.graphics().setDepth(depth);
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: durationMs,
      onUpdate: (tween) => {
        graphics.clear();
        draw(graphics, tween.getValue() ?? 0);
      },
      onComplete: () => {
        graphics.destroy();
      },
    });
  }
}

/** Fully opaque until `start`, then a linear fade to nothing. */
function fade(progress: number, start: number): number {
  return progress < start ? 1 : Math.max(0, 1 - (progress - start) / (1 - start));
}

/**
 * The warning on the ground: every tile washed dark red under sliding
 * hatching, the tiles lit brighter from the origin outward as time runs (a
 * rain's all at once, its drops being the aim), and the shape's outline —
 * only the edges no other tile of it touches — blinking faster and faster.
 */
function drawWarning(graphics: Phaser.GameObjects.Graphics, shape: Shape, p: number): void {
  const shift = Math.floor(p * 24) % 4;
  for (const tile of shape.tiles) {
    const x0 = tile.x * TILE_SIZE;
    const y0 = tile.y * TILE_SIZE;
    graphics.fillStyle(WARNING_DARK, 0.3 + p * 0.2);
    graphics.fillRect(x0, y0, TILE_SIZE, TILE_SIZE);
    graphics.fillStyle(WARNING, 0.35);
    for (let y = 0; y < TILE_SIZE; y += 1) {
      for (let x = (y + shift + x0 + y0) % 4; x < TILE_SIZE; x += 4)
        graphics.fillRect(x0 + x, y0 + y, 1, 1);
    }
    const lit = shape.kind === 'rain' ? p : p * 1.05 - cheb(tile, shape.origin) / (shape.reach + 1);
    if (lit > 0) {
      graphics.fillStyle(WARNING, Math.min(0.5, lit * 2));
      graphics.fillRect(x0, y0, TILE_SIZE, TILE_SIZE);
    }
  }
  const blinks = p < 0.5 ? 2 : p < 0.8 ? 4 : 8;
  const on = Math.floor(p * blinks * 2) % 2 === 0;
  graphics.fillStyle(on ? WARNING : WHITE, 1);
  for (const tile of shape.tiles) {
    const x0 = tile.x * TILE_SIZE;
    const y0 = tile.y * TILE_SIZE;
    const edge = (dx: number, dy: number): boolean =>
      !shape.set.has(keyOf({ x: tile.x + dx, y: tile.y + dy }));
    if (edge(0, -1)) graphics.fillRect(x0, y0, TILE_SIZE, 1);
    if (edge(0, 1)) graphics.fillRect(x0, y0 + TILE_SIZE - 1, TILE_SIZE, 1);
    if (edge(-1, 0)) graphics.fillRect(x0, y0, 1, TILE_SIZE);
    if (edge(1, 0)) graphics.fillRect(x0 + TILE_SIZE - 1, y0, 1, TILE_SIZE);
  }
}

/** A slam aims: four corner brackets close from outside the square onto its centre tile. */
function drawBrackets(
  graphics: Phaser.GameObjects.Graphics,
  centre: { x: number; y: number },
  radius: number,
  p: number,
): void {
  const inner = TILE_SIZE / 2 + 1;
  const outer = ((radius * 2 + 1) * TILE_SIZE) / 2 + 6;
  const reach = Math.round(outer - (outer - inner) * Math.min(1, p * 1.15));
  const arm = 5;
  const cx = Math.round(centre.x);
  const cy = Math.round(centre.y);
  graphics.fillStyle(WHITE, 0.9);
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      const x = cx + sx * reach;
      const y = cy + sy * reach;
      graphics.fillRect(Math.min(x, x - sx * arm), y, arm + 1, 1);
      graphics.fillRect(x, Math.min(y, y - sy * arm), 1, arm + 1);
    }
  }
}

/** A nova or a ring comes from the boss: waves run out over the shape, again and again. */
function drawWaves(
  graphics: Phaser.GameObjects.Graphics,
  shape: Shape,
  p: number,
  color: number,
): void {
  const c = px(shape.origin);
  const far = (shape.reach + 0.5) * TILE_SIZE;
  // A ring's waves start where it does: its hole stays calm.
  const start = shape.kind === 'ring' ? minReach(shape) : 0;
  const period = 0.35;
  for (const offset of [0, period / 2]) {
    const phase = ((p + offset) % period) / period;
    const r = start * TILE_SIZE + phase * (far - start * TILE_SIZE);
    ring(graphics, c.x, c.y, r, 1 - phase * 0.7, color, 1);
  }
}

/** How close to its origin a shape's nearest tile lies: a ring's hole. */
function minReach(shape: Shape): number {
  return Math.min(...shape.tiles.map((t) => cheb(t, shape.origin)));
}

/**
 * A line or a cross is thrown out from the boss: chevrons race along each
 * arm toward its end, faster as the blow nears, pointing the way it goes.
 */
function drawChevrons(graphics: Phaser.GameObjects.Graphics, shape: Shape, p: number): void {
  const arms: { dx: number; dy: number }[] = [];
  for (const [dx, dy] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    if (shape.set.has(keyOf({ x: shape.origin.x + dx, y: shape.origin.y + dy }))) {
      // A line of width 3 also covers the tiles beside its axis: only arms whose second tile is in.
      if (shape.set.has(keyOf({ x: shape.origin.x + dx * 2, y: shape.origin.y + dy * 2 }))) {
        arms.push({ dx, dy });
      }
    }
  }
  // Of a wide line only its main axis: the arm that reaches farthest.
  const chosen =
    shape.kind === 'line'
      ? arms
          .map((arm) => ({ arm, length: armLength(shape, arm) }))
          .sort((a, b) => b.length - a.length)
          .slice(0, 1)
          .map((a) => a.arm)
      : arms.filter((a) => a.dx === 0 || a.dy === 0);
  const speed = 1 + p * 3;
  graphics.fillStyle(WHITE, 0.85);
  for (const arm of chosen) {
    const length = armLength(shape, arm) * TILE_SIZE;
    const o = px(shape.origin);
    for (let k = 0; k < 3; k += 1) {
      const along = ((p * speed + k / 3) % 1) * length + TILE_SIZE / 2;
      const x = Math.round(o.x + arm.dx * along);
      const y = Math.round(o.y + arm.dy * along);
      // A chevron: two strokes meeting at the tip, pointing along the arm.
      for (let s = 0; s < 4; s += 1) {
        const bx = x - arm.dx * s;
        const by = y - arm.dy * s;
        graphics.fillRect(bx - arm.dy * s, by + arm.dx * s, 1, 1);
        graphics.fillRect(bx + arm.dy * s, by - arm.dx * s, 1, 1);
      }
    }
  }
}

/** How many tiles an arm of the shape runs from its origin, along (dx, dy). */
function armLength(shape: Shape, arm: { dx: number; dy: number }): number {
  let n = 0;
  while (
    shape.set.has(
      keyOf({ x: shape.origin.x + arm.dx * (n + 1), y: shape.origin.y + arm.dy * (n + 1) }),
    )
  )
    n += 1;
  return n;
}

/** A rain is coming: on each drop, the shadow of what falls grows darker and wider. */
function drawShadows(graphics: Phaser.GameObjects.Graphics, shape: Shape, p: number): void {
  for (const drop of shape.drops) {
    const c = px(drop);
    const rx = Math.round(2 + p * (shape.radius + 0.5) * TILE_SIZE * 0.8);
    const ry = Math.max(1, Math.round(rx * 0.5));
    graphics.fillStyle(SOOT, 0.25 + p * 0.45);
    for (let dy = -ry; dy <= ry; dy += 1) {
      const half = Math.round(rx * Math.sqrt(1 - (dy / (ry + 0.5)) ** 2));
      graphics.fillRect(Math.round(c.x) - half, Math.round(c.y) + dy, half * 2 + 1, 1);
    }
  }
}

/** Cracks in the ground under a slam: short dark rays from the centre. */
function drawCracks(
  graphics: Phaser.GameObjects.Graphics,
  c: { x: number; y: number },
  p: number,
): void {
  graphics.fillStyle(SOOT, fade(p, 0.4) * 0.9);
  const rays = 7;
  for (let ray = 0; ray < rays; ray += 1) {
    const angle = (ray / rays) * Math.PI * 2 + 0.4;
    const length = 6 + (ray % 3) * 4;
    for (let step = 2; step < length; step += 1) {
      const wobble = step % 3 === 0 ? (ray % 2 === 0 ? 1 : -1) : 0;
      graphics.fillRect(
        Math.round(c.x + Math.cos(angle) * step + wobble * Math.sin(angle)),
        Math.round(c.y + Math.sin(angle) * step * 0.6),
        1,
        1,
      );
    }
  }
}

/**
 * A ring of whole pixels, flattened like the ground, two pixels thick, white
 * while `heat` is low, then the wave's colour.
 */
function ring(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  radius: number,
  alpha: number,
  color: number,
  heat: number,
): void {
  if (radius < 1 || alpha <= 0) return;
  graphics.fillStyle(heat < 0.25 ? WHITE : color, alpha);
  const steps = Math.max(12, Math.round(radius * 2.5));
  for (let step = 0; step < steps; step += 1) {
    const angle = (step / steps) * Math.PI * 2;
    const x = Math.round(cx + Math.cos(angle) * radius);
    const y = Math.round(cy + Math.sin(angle) * radius * 0.6);
    graphics.fillRect(x, y, 1, 1);
    graphics.fillRect(x, y - 1, 1, 1);
  }
}

interface Grain {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  size: number;
}

/** Debris thrown up from the shape's tiles, up and out, then back down under gravity. */
function scatter(shape: Shape): Grain[] {
  const sources = shape.kind === 'rain' && shape.drops.length > 0 ? shape.drops : shape.tiles;
  const count = Math.min(DEBRIS_MAX, Math.max(12, sources.length * 2));
  const grains: Grain[] = [];
  for (let k = 0; k < count; k += 1) {
    const from = px(sources[k % sources.length] ?? shape.origin);
    const angle = Math.random() * Math.PI * 2;
    const speed = 10 + Math.random() * 26;
    grains.push({
      x: from.x + (Math.random() - 0.5) * TILE_SIZE,
      y: from.y + (Math.random() - 0.5) * TILE_SIZE * 0.6,
      vx: Math.cos(angle) * speed,
      vy: -(28 + Math.random() * 40),
      g: 70 + Math.random() * 30,
      size: Math.random() < 0.3 ? 2 : 1,
    });
  }
  return grains;
}
